import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Eye, FileText, ReceiptText } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import Pagination from '../common/Pagination';
import InvoiceDetailModal from '../modals/InvoiceDetailModal';

const invoiceTypeLabels = {
  Sales: 'Satış',
  Purchase: 'Alış',
  SalesReturn: 'Satış İade',
  PurchaseReturn: 'Alış İade'
};

const typeTone = {
  Sales: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  Purchase: 'bg-rose-50 text-rose-700 border-rose-100',
  SalesReturn: 'bg-amber-50 text-amber-700 border-amber-100',
  PurchaseReturn: 'bg-blue-50 text-blue-700 border-blue-100'
};

const statusTone = {
  Paid: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  PartiallyPaid: 'bg-amber-50 text-amber-700 border-amber-100',
  Unpaid: 'bg-rose-50 text-rose-700 border-rose-100',
  Cancelled: 'bg-slate-100 text-slate-500 border-slate-200'
};

const AccountInvoicesTab = ({ accountId, accountName }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    limit: 10
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);

  useEffect(() => {
    if (!accountId) {
      setInvoices([]);
      setError('');
      return undefined;
    }

    const controller = new AbortController();

    const loadInvoices = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await axios.get('/api/invoices', {
          params: {
            accountId,
            page,
            limit: 10
          },
          signal: controller.signal
        });

        const payload = response.data || {};
        setInvoices(Array.isArray(payload.data) ? payload.data : []);
        setPagination({
          totalItems: Number(payload.totalItems || 0),
          currentPage: Number(payload.currentPage || page),
          totalPages: Number(payload.totalPages || 1),
          limit: Number(payload.limit || 10)
        });
      } catch (err) {
        if (axios.isCancel(err) || err.code === 'ERR_CANCELED') return;
        setInvoices([]);
        setError(err.response?.data?.message || 'Faturalar yüklenemedi.');
      } finally {
        setLoading(false);
      }
    };

    loadInvoices();

    return () => controller.abort();
  }, [accountId, page]);

  const visibleCurrencies = useMemo(() => (
    Array.from(new Set(invoices.map((invoice) => invoice.currency || 'TRY')))
  ), [invoices]);

  useEffect(() => {
    setPage(1);
  }, [accountId]);

  const openInvoicePreview = (invoiceId) => {
    if (!invoiceId) return;
    setSelectedInvoiceId(invoiceId);
    setIsInvoiceModalOpen(true);
  };

  if (!accountId) {
    return (
      <div className="rounded-[2rem] border border-dashed border-slate-200 bg-slate-50 p-10 text-center font-bold text-slate-400">
        Fatura listesi için bir cari seçilmelidir.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3">
            <ReceiptText size={18} className="text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-800">Cari Faturaları</h2>
            <p className="text-xs font-bold text-slate-400">
              {accountName ? `${accountName} için girilmiş faturalar` : 'Bu cariye ait faturalar'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 text-sm font-black text-slate-700 sm:grid-cols-3">
          <div className="rounded-2xl bg-slate-50 px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Toplam Fatura</div>
            <div className="mt-1 text-lg text-slate-900">{pagination.totalItems || 0}</div>
          </div>
          <div className="rounded-2xl bg-blue-50 px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest text-blue-500">Sayfa</div>
            <div className="mt-1 text-lg text-blue-700">
              {pagination.currentPage || 1} / {pagination.totalPages || 1}
            </div>
          </div>
          <div className="rounded-2xl bg-emerald-50 px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest text-emerald-500">Para Birimi</div>
            <div className="mt-1 text-sm text-emerald-700">
              {visibleCurrencies.length > 0 ? visibleCurrencies.join(' / ') : 'TRY'}
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-[2rem] border border-slate-100 bg-white shadow-sm">
        {loading && (
          <div className="border-b border-slate-50 bg-slate-50/60 px-6 py-4 text-sm font-bold text-slate-500">
            Faturalar yükleniyor...
          </div>
        )}

        {error && (
          <div className="border-b border-rose-100 bg-rose-50 px-6 py-4 text-sm font-bold text-rose-600">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-400">
                <th className="p-6">Fatura No</th>
                <th className="p-6">Tip</th>
                <th className="p-6">Tarih</th>
                <th className="p-6">Para Birimi</th>
                <th className="p-6 text-right">Toplam Tutar</th>
                <th className="p-6 text-right">Kalan</th>
                <th className="p-6 text-center">Durum</th>
                <th className="p-6 text-right">Aksiyon</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {!loading && invoices.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-16">
                    <div className="mx-auto flex max-w-md flex-col items-center gap-3 rounded-[2rem] border border-dashed border-slate-200 bg-slate-50/70 px-6 py-12 text-center">
                      <FileText size={34} className="text-slate-300" />
                      <p className="text-sm font-bold text-slate-400">Bu cariye bağlı fatura bulunmuyor.</p>
                      <button
                        type="button"
                        onClick={() => navigate('/invoices/new')}
                        className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-xs font-black text-white transition-colors hover:bg-blue-700"
                      >
                        <FileText size={14} />
                        Yeni Fatura Oluştur
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => (
                  <tr
                    key={invoice._id}
                    className="cursor-pointer transition-colors hover:bg-slate-50/80"
                    onClick={() => openInvoicePreview(invoice._id)}
                  >
                    <td className="p-6">
                      <div className="font-black text-slate-800">{invoice.invoiceNumber || '-'}</div>
                      <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        {invoice.operation?.projectNo || invoice.operation?.name || 'Bağlantısız'}
                      </div>
                    </td>
                    <td className="p-6">
                      <span className={`inline-flex rounded-lg border px-3 py-1 text-[10px] font-black uppercase tracking-widest ${typeTone[invoice.type] || 'bg-slate-50 text-slate-600 border-slate-100'}`}>
                        {invoiceTypeLabels[invoice.type] || invoice.type || '-'}
                      </span>
                    </td>
                    <td className="p-6 font-bold text-slate-600">
                      {invoice.date ? new Date(invoice.date).toLocaleDateString('tr-TR') : '-'}
                    </td>
                    <td className="p-6 font-black text-slate-700">
                      {invoice.currency || 'TRY'}
                    </td>
                    <td className="p-6 text-right font-black text-slate-800">
                      {formatCurrency(invoice.grandTotal || 0, invoice.currency || 'TRY')}
                    </td>
                    <td className="p-6 text-right font-black text-rose-600">
                      {formatCurrency(invoice.remainingAmount || 0, invoice.currency || 'TRY')}
                    </td>
                    <td className="p-6 text-center">
                      <span className={`inline-flex rounded-lg border px-3 py-1 text-[10px] font-black uppercase tracking-widest ${statusTone[invoice.paymentStatus] || statusTone.Unpaid}`}>
                        {invoice.paymentStatus || 'Unpaid'}
                      </span>
                    </td>
                    <td className="p-6 text-right">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          openInvoicePreview(invoice._id);
                        }}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                        title="Faturayı önizle"
                        aria-label="Faturayı önizle"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={pagination.currentPage || page}
          totalPages={pagination.totalPages || 1}
          onPageChange={setPage}
        />
      </div>

      <InvoiceDetailModal
        isOpen={isInvoiceModalOpen}
        onClose={() => {
          setIsInvoiceModalOpen(false);
          setSelectedInvoiceId(null);
        }}
        invoiceId={selectedInvoiceId}
      />
    </div>
  );
};

export default AccountInvoicesTab;
